import React, { useRef, useEffect, useCallback, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { usePlatformAuth } from '@/contexts/PlatformAuthContext';
import { TipWidget } from '@/components/tips/TipWidget';
import { JoinButton, JoinPill, useRotatingUrgency } from '@/components/common/JoinCta';
import SectionGlow from '@/components/common/SectionGlow';
import { Heart } from 'lucide-react';
import { Howl } from 'howler';
import { useJoinPrice } from '@/hooks/useJoinPrice';

const HERO_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQS = [261.6, 293.7, 329.6, 392.0, 440.0, 523.3, 587.3, 659.3, 784.0];
const soundCache = {};
function playNote(freq) {
  if (!soundCache[freq]) {
    const sr = 22050, dur = 0.5, len = sr * dur;
    const buf = new Float32Array(len);
    for (let i = 0; i < len; i++) { const t = i / sr; buf[i] = Math.sin(2 * Math.PI * freq * t) * Math.exp(-t * 5) * 0.12; }
    const ab = new ArrayBuffer(44 + len * 2); const v = new DataView(ab);
    const ws = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
    ws(0,'RIFF'); v.setUint32(4,36+len*2,true); ws(8,'WAVE'); ws(12,'fmt ');
    v.setUint32(16,16,true); v.setUint16(20,1,true); v.setUint16(22,1,true);
    v.setUint32(24,sr,true); v.setUint32(28,sr*2,true); v.setUint16(32,2,true); v.setUint16(34,16,true);
    ws(36,'data'); v.setUint32(40,len*2,true);
    let off = 44;
    for (let i = 0; i < len; i++) { const s = Math.max(-1, Math.min(1, buf[i])); v.setInt16(off, s<0?s*0x8000:s*0x7FFF, true); off+=2; }
    soundCache[freq] = new Howl({ src:[URL.createObjectURL(new Blob([ab],{type:'audio/wav'}))], format:['wav'], volume:0.18 });
  }
  soundCache[freq].play();
}

// Bluebird blue palette, resolved from theme tokens (never the old gold or indigo).
let GLOW_COLORS = ['#2575FC', '#1A65E4', '#5B9BFF', '#0A3680', '#4C8DFF'];
try {
  const _root = getComputedStyle(document.documentElement);
  const _a = _root.getPropertyValue('--accent-primary').trim();
  const _hover = _root.getPropertyValue('--accent-hover').trim();
  const _deep = _root.getPropertyValue('--accent-deep').trim();
  if (_a) GLOW_COLORS = [_a, _hover || '#1A65E4', '#5B9BFF', _deep || '#0A3680', '#4C8DFF'];
} catch (e) { /* noop, falls back to blue defaults */ }

// Symbol types for different geometric needs
const SYM = {
  beam:   ['\u266B', '\u266C'],           // beamed notes for straight strokes
  single: ['\u2669', '\u266A'],           // single notes for fill/gaps
  flat:   ['\u266D'],                      // for curves
  sharp:  ['\u266F'],                      // for crossbars/grids
  natural:['\u266E'],                      // for verticals
};

// Hand-crafted letter definitions. Each point: [relX, relY, symbolSet, sizeMultiplier, rotation]
// Coordinates 0-1 within each letter bounding box
const LETTERS = {
  S: [
    // Top curve (right to left)
    [0.75, 0.08, 'flat', 1.1, 0], [0.45, 0.03, 'single', 1.0, 0], [0.20, 0.10, 'flat', 1.2, 0],
    // Upper body
    [0.15, 0.22, 'beam', 0.9, -0.2], [0.25, 0.35, 'single', 1.0, 0],
    // Middle S-curve
    [0.40, 0.42, 'sharp', 1.1, 0], [0.55, 0.50, 'single', 1.0, 0], [0.70, 0.58, 'sharp', 1.0, 0],
    // Lower body
    [0.80, 0.68, 'beam', 0.9, 0.2], [0.75, 0.78, 'single', 1.0, 0],
    // Bottom curve (left to right)
    [0.55, 0.90, 'flat', 1.1, 0], [0.28, 0.88, 'single', 1.0, 0], [0.15, 0.80, 'flat', 1.2, 0],
    // Extra fill
    [0.60, 0.15, 'single', 0.8, 0], [0.35, 0.70, 'single', 0.8, 0],
    [0.50, 0.25, 'beam', 0.9, 0], [0.45, 0.65, 'beam', 0.9, 0],
    [0.80, 0.45, 'natural', 0.9, 0], [0.20, 0.55, 'natural', 0.9, 0],
  ],
  A: [
    // Left diagonal stroke (bottom-left to top)
    [0.08, 0.92, 'beam', 1.0, -0.3], [0.15, 0.72, 'beam', 1.0, -0.3], [0.23, 0.52, 'beam', 1.0, -0.3],
    [0.32, 0.32, 'beam', 1.1, -0.3], [0.42, 0.12, 'single', 1.0, 0],
    // Peak
    [0.50, 0.03, 'single', 1.2, 0],
    // Right diagonal stroke (top to bottom-right)
    [0.58, 0.12, 'single', 1.0, 0], [0.68, 0.32, 'beam', 1.1, 0.3],
    [0.77, 0.52, 'beam', 1.0, 0.3], [0.85, 0.72, 'beam', 1.0, 0.3], [0.92, 0.92, 'beam', 1.0, 0.3],
    // Horizontal crossbar
    [0.28, 0.60, 'sharp', 1.0, 0], [0.42, 0.58, 'sharp', 1.1, 0], [0.58, 0.58, 'sharp', 1.1, 0], [0.72, 0.60, 'sharp', 1.0, 0],
    // Fill
    [0.35, 0.45, 'single', 0.8, 0], [0.65, 0.45, 'single', 0.8, 0],
    [0.50, 0.30, 'single', 0.7, 0], [0.45, 0.75, 'single', 0.7, 0], [0.55, 0.75, 'single', 0.7, 0],
  ],
  M: [
    // Left vertical
    [0.05, 0.92, 'beam', 1.0, 0], [0.05, 0.70, 'natural', 1.1, 0], [0.05, 0.48, 'beam', 1.0, 0],
    [0.05, 0.26, 'natural', 1.1, 0], [0.05, 0.05, 'beam', 1.0, 0],
    // Left diagonal down
    [0.15, 0.15, 'beam', 0.9, 0.4], [0.25, 0.32, 'beam', 1.0, 0.4], [0.35, 0.48, 'single', 1.0, 0.3],
    [0.45, 0.62, 'single', 1.0, 0.2],
    // Center valley
    [0.50, 0.70, 'single', 1.1, 0],
    // Right diagonal up
    [0.55, 0.62, 'single', 1.0, -0.2], [0.65, 0.48, 'beam', 1.0, -0.4],
    [0.75, 0.32, 'beam', 0.9, -0.4], [0.85, 0.15, 'beam', 0.9, -0.4],
    // Right vertical
    [0.95, 0.05, 'beam', 1.0, 0], [0.95, 0.26, 'natural', 1.1, 0], [0.95, 0.48, 'beam', 1.0, 0],
    [0.95, 0.70, 'natural', 1.1, 0], [0.95, 0.92, 'beam', 1.0, 0],
    // Fill
    [0.20, 0.50, 'single', 0.7, 0], [0.80, 0.50, 'single', 0.7, 0],
    [0.40, 0.35, 'single', 0.7, 0], [0.60, 0.35, 'single', 0.7, 0],
  ],
  T: [
    // Horizontal top bar
    [0.05, 0.05, 'beam', 1.0, 0], [0.20, 0.05, 'sharp', 1.1, 0], [0.35, 0.05, 'beam', 1.0, 0],
    [0.50, 0.05, 'sharp', 1.2, 0], [0.65, 0.05, 'beam', 1.0, 0],
    [0.80, 0.05, 'sharp', 1.1, 0], [0.95, 0.05, 'beam', 1.0, 0],
    // Extra top thickness
    [0.12, 0.15, 'single', 0.8, 0], [0.35, 0.15, 'single', 0.8, 0],
    [0.65, 0.15, 'single', 0.8, 0], [0.88, 0.15, 'single', 0.8, 0],
    // Vertical stem
    [0.50, 0.20, 'natural', 1.1, 0], [0.50, 0.35, 'beam', 1.0, 0],
    [0.50, 0.50, 'natural', 1.2, 0], [0.50, 0.65, 'beam', 1.0, 0],
    [0.50, 0.80, 'natural', 1.1, 0], [0.50, 0.92, 'beam', 1.0, 0],
    // Stem thickness
    [0.42, 0.40, 'single', 0.7, 0], [0.58, 0.40, 'single', 0.7, 0],
    [0.42, 0.70, 'single', 0.7, 0], [0.58, 0.70, 'single', 0.7, 0],
  ],
  W: [
    // Left vertical
    [0.02, 0.05, 'beam', 1.0, 0], [0.05, 0.25, 'natural', 1.0, 0], [0.08, 0.45, 'beam', 1.0, 0.15],
    [0.12, 0.65, 'natural', 1.0, 0.15], [0.16, 0.85, 'beam', 1.0, 0.15],
    // Left V down
    [0.20, 0.92, 'single', 1.1, 0],
    [0.24, 0.78, 'beam', 0.9, -0.3], [0.30, 0.60, 'beam', 1.0, -0.3],
    // Center peak
    [0.38, 0.42, 'single', 1.1, 0], [0.45, 0.32, 'single', 1.0, 0], [0.50, 0.28, 'single', 1.2, 0],
    [0.55, 0.32, 'single', 1.0, 0], [0.62, 0.42, 'single', 1.1, 0],
    // Right V down
    [0.70, 0.60, 'beam', 1.0, 0.3], [0.76, 0.78, 'beam', 0.9, 0.3],
    [0.80, 0.92, 'single', 1.1, 0],
    // Right vertical
    [0.84, 0.85, 'beam', 1.0, -0.15], [0.88, 0.65, 'natural', 1.0, -0.15],
    [0.92, 0.45, 'beam', 1.0, -0.15], [0.95, 0.25, 'natural', 1.0, 0], [0.98, 0.05, 'beam', 1.0, 0],
    // Fill
    [0.35, 0.65, 'single', 0.7, 0], [0.65, 0.65, 'single', 0.7, 0],
  ],
  O: [
    // Top arc
    [0.50, 0.02, 'flat', 1.2, 0], [0.30, 0.05, 'single', 1.0, 0], [0.70, 0.05, 'single', 1.0, 0],
    [0.18, 0.12, 'flat', 1.0, -0.2], [0.82, 0.12, 'flat', 1.0, 0.2],
    // Left side
    [0.08, 0.28, 'natural', 1.1, 0], [0.05, 0.45, 'beam', 1.0, 0],
    [0.05, 0.60, 'natural', 1.1, 0], [0.08, 0.75, 'beam', 1.0, 0],
    // Bottom arc
    [0.18, 0.88, 'flat', 1.0, 0.2], [0.30, 0.94, 'single', 1.0, 0],
    [0.50, 0.97, 'flat', 1.2, 0], [0.70, 0.94, 'single', 1.0, 0],
    [0.82, 0.88, 'flat', 1.0, -0.2],
    // Right side
    [0.92, 0.75, 'beam', 1.0, 0], [0.95, 0.60, 'natural', 1.1, 0],
    [0.95, 0.45, 'beam', 1.0, 0], [0.92, 0.28, 'natural', 1.1, 0],
    // Inner fill
    [0.35, 0.35, 'single', 0.7, 0], [0.65, 0.35, 'single', 0.7, 0],
    [0.35, 0.65, 'single', 0.7, 0], [0.65, 0.65, 'single', 0.7, 0],
    [0.50, 0.50, 'sharp', 0.8, 0],
  ],
};

const ParticleHero = ({ minimal = false }) => {
  const canvasRef = useRef(null);
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];
  const { everSubscribed } = usePlatformAuth();
  const { joinPrice: ctaPrice } = useJoinPrice({ skip: minimal });
  const [hero, setHero] = useState(null);
  const [social, setSocial] = useState({ count: 0 });
  useEffect(() => {
    // Minimal (pre-launch landing) mode: these data APIs are gated (403) before
    // launch, so skip them entirely and render just the wordmark/playable notes.
    if (minimal) return;
    axios.get(`${HERO_API}/home/hero`, { params: { lang } }).then((r) => setHero(r.data)).catch(() => {});
    axios.get(`${HERO_API}/live/subscribers/count`).then((r) => setSocial(r.data)).catch(() => {});
  }, [lang, minimal]);
  // Resolve admin-typed {{price}} in any CMS hero copy to the live join price.
  const resolveCopy = (s) => (typeof s === 'string' ? s.replace(/\{\{\s*price\s*\}\}/g, ctaPrice || '') : s);
  const urgency = useRotatingUrgency();
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const animFrameRef = useRef(null);
  const lastSoundRef = useRef(0);

  const initParticles = useCallback((canvas) => {
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    ctx.scale(dpr, dpr);

    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;

    // Measure each letter's position using the heavy font
    const fontSize = Math.min(w * 0.17, 220);
    ctx.font = `700 ${fontSize}px "Marko One", Georgia, serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    const word = 'SAMTWO';
    // Calculate total width including extra spacing first
    const letters = word.split('');
    let totalWidth = 0;
    for (let i = 0; i < letters.length; i++) {
      totalWidth += ctx.measureText(letters[i]).width;
      if (i < letters.length - 1) {
        totalWidth += (i === 0) ? fontSize * 0.06 : fontSize * 0.12;
      }
    }
    const startX = (w - totalWidth) / 2;
    const centerY = h * 0.40;
    const letterHeight = fontSize * 0.85;
    const noteBaseSize = Math.max(fontSize / 6, 18);

    // Get each letter's x offset
    let xOff = startX;
    const letterBoxes = [];
    for (let i = 0; i < letters.length; i++) {
      const ch = letters[i];
      const m = ctx.measureText(ch);
      const lw = m.width;
      letterBoxes.push({ char: ch, x: xOff, y: centerY - letterHeight / 2, w: lw, h: letterHeight });
      // Keep S-A spacing as is, add extra between A-M, M-T, T-W, W-O
      const spacing = (i === 0) ? fontSize * 0.06 : fontSize * 0.12;
      xOff += lw + spacing;
    }

    // Build particles from hand-crafted definitions
    const particles = [];
    for (const box of letterBoxes) {
      const def = LETTERS[box.char];
      if (!def) continue;
      for (const pt of def) {
        const [rx, ry, symSet, sizeMul, rot] = pt;
        const px = box.x + rx * box.w;
        const py = box.y + ry * box.h;
        const symbols = SYM[symSet];
        const note = symbols[Math.floor(Math.random() * symbols.length)];
        const colorIdx = Math.floor(Math.random() * GLOW_COLORS.length);
        const freqIdx = Math.floor(Math.random() * FREQS.length);

        particles.push({
          x: px, y: py,
          origX: px, origY: py,
          vx: 0, vy: 0,
          size: noteBaseSize * sizeMul,
          note,
          color: GLOW_COLORS[colorIdx],
          freq: FREQS[freqIdx],
          rotation: rot || 0,
          played: false,
        });
      }
    }
    return particles;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => { particlesRef.current = initParticles(canvas); };
    resize();
    window.addEventListener('resize', resize);

    const handleMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      mouseRef.current = { x: cx - rect.left, y: cy - rect.top };
    };
    const handleLeave = () => { mouseRef.current = { x: -9999, y: -9999 }; };

    canvas.addEventListener('mousemove', handleMove);
    canvas.addEventListener('touchmove', handleMove, { passive: true });
    canvas.addEventListener('mouseleave', handleLeave);
    canvas.addEventListener('touchend', handleLeave);

    const animate = () => {
      const dpr = window.devicePixelRatio || 1;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const now = Date.now();
      const radius = 110;

      for (const p of particlesRef.current) {
        const dx = p.x - mx;
        const dy = p.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < radius) {
          const force = (radius - dist) / radius;
          const angle = Math.atan2(dy, dx);
          p.vx += Math.cos(angle) * force * 16;
          p.vy += Math.sin(angle) * force * 16;
          if (!p.played && now - lastSoundRef.current > 80) {
            playNote(p.freq);
            lastSoundRef.current = now;
            p.played = true;
          }
        } else {
          p.played = false;
        }

        p.vx += (p.origX - p.x) * 0.07;
        p.vy += (p.origY - p.y) * 0.07;
        p.vx *= 0.87;
        p.vy *= 0.87;
        p.x += p.vx;
        p.y += p.vy;

        const distFromOrig = Math.sqrt((p.x - p.origX) ** 2 + (p.y - p.origY) ** 2);
        const isScattered = distFromOrig > 4;

        ctx.save();
        ctx.font = `900 ${p.size}px serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        if (isScattered) {
          const glowIntensity = Math.min(distFromOrig / 25, 1);
          ctx.shadowBlur = 22 * glowIntensity;
          ctx.shadowColor = p.color;
          ctx.fillStyle = p.color;
          ctx.globalAlpha = 0.95;
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation + (Math.random() - 0.5) * 0.1 * glowIntensity);
          ctx.fillText(p.note, 0, 0);
        } else {
          ctx.shadowBlur = 8;
          ctx.shadowColor = 'rgba(37, 117, 252, 0.4)';
          ctx.fillStyle = 'rgba(37, 117, 252, 0.55)';
          ctx.globalAlpha = 0.95;
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation);
          ctx.fillText(p.note, 0, 0);
        }
        ctx.restore();
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMove);
      canvas.removeEventListener('touchmove', handleMove);
      canvas.removeEventListener('mouseleave', handleLeave);
      canvas.removeEventListener('touchend', handleLeave);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [initParticles]);

  return (
    <section data-testid="hero-section" className={`relative ${minimal ? 'h-[60vh] min-h-[420px]' : 'h-screen'} flex flex-col items-center justify-center overflow-hidden`} style={{ background: 'transparent' }}>
      <SectionGlow position="50% 35%" strength={12} />
      <canvas
        ref={canvasRef}
        data-testid="particle-canvas"
        className="w-full absolute inset-0"
        style={{ height: '100%', cursor: 'pointer' }}
      />
      {!minimal && (
      <div className="relative z-10 flex flex-col items-center pointer-events-none gap-5" style={{ marginTop: '34vh' }}>
        <p data-testid="hero-subline" className="text-base sm:text-lg font-medium text-center px-6" style={{ color: 'var(--text-secondary)' }}>
          {resolveCopy(hero?.subline) || "Don't just follow. Become his boss."}
        </p>
        {!everSubscribed && social.visible && social.count > 0 && (
          <div data-testid="hero-social-proof" className="flex flex-col items-center gap-1.5 pointer-events-none">
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
                <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ background: 'var(--success)' }} />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: 'var(--success)' }} />
              </span>
              <p className="text-sm sm:text-base" style={{ color: 'var(--text-primary)' }} data-testid="hero-count">
                <span className="font-semibold">{social.count.toLocaleString()}</span>{' '}
                <span style={{ color: 'var(--text-secondary)' }}>{social.count === 1 ? t('home.hero.people_inside_one') : t('home.hero.people_inside_other')}</span>
              </p>
            </div>
            {urgency && <p className="text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="hero-urgency">{resolveCopy(urgency)}</p>}
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <JoinButton source="hero" testId="hero-primary-cta" />
          <TipWidget
            sourceType="homepage"
            compact
            trigger={({ onClick }) => (
              <button
                data-testid="hero-tip-cta"
                onClick={onClick}
                className="pointer-events-auto btn-pill metallic-sheen text-sm sm:text-base"
                style={{ color: '#fff' }}
              >
                <Heart size={15} /> {t('home.story.send_tip')}
              </button>
            )}
          />
        </div>
        <JoinPill />
      </div>
      )}
    </section>
  );
};

export default ParticleHero;
